import { useWebSocket, type UseWebSocketReturn } from '@vueuse/core';

// This module owns only the low-level WebSocket transport.
// It intentionally does not import Pinia stores, auth services, router, or UI helpers.
// Application behavior is injected through these callbacks by the websocket plugin.
interface WebSocketClientHandlers {
    onConnected?: (ws: WebSocket) => void;
    onDisconnected?: () => void;
    onError?: (event: Event) => void;
    onMessage?: (message: unknown) => void;
}

// The current VueUse websocket instance. Keeping it module-scoped gives the app
// one shared connection and lets non-component code send messages without
// depending on Vue component lifecycle.
let socket: UseWebSocketReturn<unknown> | null = null;

// The latest callback set registered by the plugin.
// Manual reconnects from the store may call connectWebSocket(url) without passing
// handlers again; in that case the client reuses these callbacks so incoming
// messages still reach the store.
let activeHandlers: WebSocketClientHandlers = {};

// Guards against stale callbacks from an old socket.
// Example: socket A is closing, socket B is already opened, then A fires onclose.
// Without this id check, A's onclose would mark B as disconnected in the store.
let activeConnectionId = 0;

export function setWebSocketHandlers(handlers: WebSocketClientHandlers) {
    activeHandlers = handlers;
}

export function connectWebSocket(connectionUrl: string, handlers: WebSocketClientHandlers = {}) {
    // Register new handlers only when they are explicitly provided.
    // This keeps reconnect calls short while still preserving the plugin/store bridge.
    if (Object.keys(handlers).length > 0) {
        activeHandlers = handlers;
    }

    // Always close the previous socket before opening a new one.
    // This prevents duplicate connections after login, token refresh, or manual recovery.
    closeCurrentSocket();

    // Capture the id for this concrete connection attempt.
    // Every callback below compares against the latest id before doing anything.
    const connectionId = ++activeConnectionId;

    socket = useWebSocket(connectionUrl, {
        autoReconnect: {
            retries: -1,
            delay: 1000,
        },
        onConnected(ws) {
            // Ignore callbacks from a socket that is no longer current.
            if (connectionId !== activeConnectionId) return;
            activeHandlers.onConnected?.(ws);
        },
        onDisconnected() {
            // Ignore callbacks from a socket that is no longer current.
            if (connectionId !== activeConnectionId) return;
            activeHandlers.onDisconnected?.();
        },
        onError(_, event) {
            // Ignore callbacks from a socket that is no longer current.
            if (connectionId !== activeConnectionId) return;
            activeHandlers.onError?.(event);
        },
        onMessage(_, event) {
            // Ignore callbacks from a socket that is no longer current.
            if (connectionId !== activeConnectionId) return;

            try {
                // The backend sends application-level JSON messages:
                // { event: string, data?: unknown }.
                // Keep parsing here so the store receives domain messages, not browser events.
                activeHandlers.onMessage?.(JSON.parse(event.data));
            } catch (error) {
                console.error('Unable to parse websocket message', error, event.data);
            }
        },
    });
}

export function disconnectWebSocket() {
    // Invalidate callbacks from the socket being closed. Browser close/error events
    // can still arrive asynchronously after close() is called.
    activeConnectionId++;
    closeCurrentSocket();
}

function closeCurrentSocket() {
    // VueUse close() is safe to call for OPEN, CONNECTING, and already-closing sockets.
    socket?.close();
    socket = null;
}

export function getWebSocketReadyState() {
    // Returns the native WebSocket readyState:
    // 0 CONNECTING, 1 OPEN, 2 CLOSING, 3 CLOSED, or undefined when no socket exists.
    return socket?.ws.value?.readyState;
}

export function isWebSocketOpen() {
    return getWebSocketReadyState() === WebSocket.OPEN;
}

export function sendWebSocketMessage(message: string) {
    // Do not buffer here. The old code only sent when the native socket was OPEN,
    // so callers keep the same behavior and can log failed sends.
    if (!isWebSocketOpen()) return false;

    socket?.ws.value?.send(message);

    return true;
}

let userData: Record<string, unknown> = {};

export function webSocketClientSendObj(obj: object): boolean {
    if (!sendWebSocketMessage(JSON.stringify(obj))) {
        console.log('Unable to send message ' + JSON.stringify(obj) + ' due to WS is not connected');
        return false;
    }
    return true;
}

export function webSocketClientSetUserData(data: Record<string, unknown>): void {
    userData = data;
}

export function webSocketClientGetUserData(): Record<string, unknown> {
    return userData;
}
